import { createSelector } from '@reduxjs/toolkit';

import {
    EMPTY_LIST,
    EMPTY_OBJECT,
    selectCheckInsEnabledCareTypes,
    selectFacility,
    selectGen3Features,
    selectIsConfigReady,
    selectIsObservationsEnabled,
    selectObservationDetailsEnabledCareTypes,
    selectObservationsByCareType,
} from './configSlice';
import {
    EhrFeature,
    EhrFeatureType,
    EhrProvider,
    EhrProviderConfig,
    Gen3Feature,
    Gen3FeatureType,
    ObservationType,
    ResourceField,
} from './sageAdminApi';
import { CareType, CareTypes } from 'src/constants/CareTypes';

/**
 * Resolves every facility config feature that can be scoped to a care type (EHR, check-ins, observations, Gen3).
 * Prefer the `useCareTypeFeatures` hook over reading these selectors directly.
 */

// Represents the EHR-related configuration for a specific care type
export interface EhrCareTypeConfig {
    isResidentProfileSyncEnabled: boolean;
    isTaskingEnabled: boolean;
    lockedFields: Set<ResourceField>;
    providerName?: EhrProvider;
}

export type Gen3CareTypeConfig = Record<Gen3FeatureType, { isEnabled: boolean }>;

const DisabledGen3Features: Gen3CareTypeConfig = {
    WANDER: { isEnabled: false },
};

const Gen3FeatureTypes = Object.keys(DisabledGen3Features) as Gen3FeatureType[];

const isGen3FeatureEnabled = (
    gen3Features: Gen3Feature[],
    featureType: Gen3FeatureType,
    careType: CareType
): boolean =>
    gen3Features.some(
        (feature) =>
            feature.featureType === featureType &&
            feature.isEnabled === true &&
            // An empty or missing list means the feature applies to every care type
            (!feature.enabledForCareTypes ||
                feature.enabledForCareTypes.length === 0 ||
                feature.enabledForCareTypes.includes(careType))
    );

const getGen3ConfigForCareType = (
    gen3Features: Gen3Feature[],
    careType: CareType
): Gen3CareTypeConfig =>
    Object.fromEntries(
        Gen3FeatureTypes.map((featureType) => [
            featureType,
            { isEnabled: isGen3FeatureEnabled(gen3Features, featureType, careType) },
        ])
    ) as Gen3CareTypeConfig;

export const selectGen3ConfigByCareType = createSelector(
    selectGen3Features,
    (gen3Features): Record<CareType, Gen3CareTypeConfig> =>
        Object.fromEntries(
            (Object.values(CareTypes) as CareType[]).map((careType) => [
                careType,
                getGen3ConfigForCareType(gen3Features, careType),
            ])
        ) as Record<CareType, Gen3CareTypeConfig>
);

export const selectCheckInsEnabledSet = createSelector(
    selectCheckInsEnabledCareTypes,
    (careTypes) => new Set([CareTypes.IndependentLiving, ...careTypes])
);

export const selectEhrConfigByCareType = createSelector(
    [selectFacility, selectIsConfigReady],
    (facility, isConfigReady): Record<CareType, EhrCareTypeConfig> => {
        if (!isConfigReady) {
            return {} as Record<CareType, EhrCareTypeConfig>;
        }

        const ehrLockedFields = facility?.ehrLockedFields ?? EMPTY_LIST;
        const ehrIntegrationsConfig: Record<string, EhrProviderConfig> =
            facility?.facilityConfig?.ehrIntegrationsConfig ?? EMPTY_OBJECT;

        const checkIfFeatureEnabled = (
            features: EhrFeature[],
            featureToCheck: EhrFeatureType,
            careTypeToCheck: string
        ): boolean =>
            features.some((feature) => {
                const isCareTypeApplicable =
                    !feature.enabledForCareTypes ||
                    feature.enabledForCareTypes.length === 0 ||
                    feature.enabledForCareTypes.includes(careTypeToCheck);

                return (
                    isCareTypeApplicable &&
                    feature.featureType === featureToCheck &&
                    feature.isEnabled
                );
            });

        const ehrConfigByCareType = (Object.values(CareTypes) as CareType[]).reduce(
            // For each care type (ex. 'ASSISTED_LIVING')
            (config, careType) => {
                // Find the first EHR provider (ex. 'ALIS') that applies to this careType and has sync enabled
                const ehrSyncedProviderEntry = Object.entries(ehrIntegrationsConfig).find(
                    ([, providerConfig]) =>
                        (!providerConfig.enabledForCareTypes ||
                            providerConfig.enabledForCareTypes.length === 0 ||
                            providerConfig.enabledForCareTypes.includes(careType)) &&
                        providerConfig.isResidentProfileSyncEnabled === true
                );

                let isResidentProfileSyncEnabled = false;
                let isTaskingEnabled = false;
                let applicableLockedFields: ResourceField[] = [];
                let providerName: EhrProvider | undefined;

                if (ehrSyncedProviderEntry) {
                    const [ehrName, providerConfig] = ehrSyncedProviderEntry;
                    // ehrIntegrationsConfig is keyed by EHR provider name
                    providerName = ehrName as EhrProvider;
                    isResidentProfileSyncEnabled = true;
                    isTaskingEnabled = checkIfFeatureEnabled(
                        providerConfig.features ?? EMPTY_LIST,
                        'TASKING',
                        careType
                    );
                    applicableLockedFields = ehrLockedFields
                        .filter((field) => field.ehrProvider === ehrName)
                        .map((field) => field.resourceField)
                        .filter((field): field is ResourceField => field !== undefined);
                }

                return {
                    ...config,
                    // TODO - For v0 this should be fine, but if there are two EHR configs with overlapping care types
                    // and different configs it will overwrite! This would be a weird config case but is worth noting.
                    [careType]: {
                        isResidentProfileSyncEnabled,
                        isTaskingEnabled,
                        lockedFields: new Set(
                            isResidentProfileSyncEnabled ? applicableLockedFields : []
                        ),
                        providerName,
                    },
                };
            },
            {} as Record<CareType, EhrCareTypeConfig>
        );
        return ehrConfigByCareType;
    }
);

export const selectIsTaskingEnabledForAtLeastOneCareType = createSelector(
    selectEhrConfigByCareType,
    (ehrConfigByCareType) =>
        Object.values(ehrConfigByCareType).some((config) => config.isTaskingEnabled)
);

// Every facility config feature that can be scoped to a care type, resolved for a single care type
export interface CareTypeFeatures {
    ehr: EhrCareTypeConfig;
    checkIns: {
        isEnabled: boolean;
    };
    observations: {
        // Facility has observations on and the care type has options configured
        isEnabled: boolean;
        isDetailsEnabled: boolean;
        options: ObservationType[];
    };
    gen3: Gen3CareTypeConfig;
}

// Default, returned for unknown / missing care types (and before config is ready) so callers never null-check
export const DisabledCareTypeFeatures: CareTypeFeatures = Object.freeze({
    ehr: {
        isResidentProfileSyncEnabled: false,
        isTaskingEnabled: false,
        lockedFields: new Set<ResourceField>(),
        providerName: undefined,
    },
    checkIns: { isEnabled: false },
    observations: { isEnabled: false, isDetailsEnabled: false, options: EMPTY_LIST },
    gen3: DisabledGen3Features,
});

export const selectCareTypeFeatures = createSelector(
    [
        selectEhrConfigByCareType,
        selectCheckInsEnabledSet,
        selectIsObservationsEnabled,
        selectObservationsByCareType,
        selectObservationDetailsEnabledCareTypes,
        selectGen3ConfigByCareType,
    ],
    (
        ehrConfigByCareType,
        checkInsEnabledSet,
        isObservationsEnabled,
        observationsByCareType,
        observationDetailsEnabledCareTypes,
        gen3ConfigByCareType
    ): Partial<Record<CareType, CareTypeFeatures>> =>
        Object.fromEntries(
            (Object.values(CareTypes) as CareType[]).map((careType) => {
                const observationOptions = observationsByCareType[careType] ?? EMPTY_LIST;

                const features: CareTypeFeatures = {
                    ehr: ehrConfigByCareType[careType] ?? DisabledCareTypeFeatures.ehr,
                    checkIns: {
                        isEnabled: checkInsEnabledSet.has(careType),
                    },
                    observations: {
                        isEnabled: isObservationsEnabled && observationOptions.length > 0,
                        isDetailsEnabled: observationDetailsEnabledCareTypes.includes(careType),
                        options: observationOptions,
                    },
                    gen3: gen3ConfigByCareType[careType] ?? DisabledCareTypeFeatures.gen3,
                };

                return [careType, features];
            })
        )
);
